/**
 * The one way the frontend talks to the backend.
 *
 * Adding an endpoint: the method goes into the hand-maintained Api.ts; its entry goes into the
 * XApi.ts of its controller as apiQueryOptions(key, (api) => api.x.y(...)) or
 * apiMutationOptions((api) => api.x.y); screens consume it with useQuery / useMutation / the
 * loaders' ensureQueryData. Never import the Api class: `api` is the one of the QueryClient that
 * runs the call, i.e. the request's own on the server and the app's in the browser. Keys are plain;
 * the QueryClient keeps them apart per signed-in identity (runtime/queryClient.ts). Errors always
 * arrive as a BaseApiResponse (unwrap.ts). Ambient context is read for labels on log lines only,
 * never for credentials. The architecture test in __tests__ enforces this.
 */
import { AccountApi } from './AccountApi';
import { ApiKeysApi } from './ApiKeysApi';
import { AccountInviteApi } from './AccountInviteApi';
import { AccountInvitesApi } from './AccountInvitesApi';
import { AccountManagementApi } from './AccountManagementApi';
import { AnalyticsApi } from './AnalyticsApi';
import { AnonymousApi } from './AnonymousApi';
import { BlacklistApi } from './BlacklistApi';
import { ChapterApi } from './ChapterApi';
import { ChatApi } from './ChatApi';
import { ChatLinkSecurityApi } from './ChatLinkSecurityApi';
import { DashboardApi } from './DashboardApi';
import { EmailListsApi } from './EmailListsApi';
import { MarketingCampaignsApi } from './MarketingCampaignsApi';
import { EmailPreferencesApi } from './EmailPreferencesApi';
import { EmailStyleApi } from './EmailStyleApi';
import { EmailTemplateApi } from './EmailTemplateApi';
import { ExamApi } from './ExamApi';
import { FollowUpApi } from './FollowUpApi';
import { InsightsApi } from './InsightsApi';
import { InterestApi } from './InterestApi';
import { InvitesApi } from './InvitesApi';
import { LiveTranscribeApi } from './LiveTranscribeApi';
import { LiveWebinarApi } from './LiveWebinarApi';
import { LoginApi } from './LoginApi';
import { OnDemandWebinarApi } from './OnDemandWebinarApi';
import { PollApi } from './PollApi';
import { SlideDeckApi } from './SlideDeckApi';
import { SubtitlesApi } from './SubtitlesApi';
import { SubtitlesGeneratorApi } from './SubtitlesGeneratorApi';
import { SurveyApi } from './SurveyApi';
import { TestSuiteApi } from './TestSuiteApi';
import { TierApi } from './TierApi';
import { TranslationApi } from './TranslationApi';
import { UnsubscribeApi } from './UnsubscribeApi';
import { UsageStatisticsApi } from './UsageStatisticsApi';
import { VideoEditorApi } from './VideoEditorApi';
import { WatchLinkApi } from './WatchLinkApi';
import { WebinarApi } from './WebinarApi';
import { WebinarAttachmentsApi } from './WebinarAttachmentsApi';
import { WebinarsApi } from './WebinarsApi';
import { WebinarSecurityApi } from './WebinarSecurityApi';

export const OptionsApi = {
    account: AccountApi,
    apiKeys: ApiKeysApi,
    accountInvite: AccountInviteApi,
    accountInvites: AccountInvitesApi,
    accountManagement: AccountManagementApi,
    anonymous: AnonymousApi,
    blacklist: BlacklistApi,
    chapter: ChapterApi,
    chat: ChatApi,
    chatLinkSecurity: ChatLinkSecurityApi,
    dashboard: DashboardApi,
    emailLists: EmailListsApi,
    marketingCampaigns: MarketingCampaignsApi,
    emailPreferences: EmailPreferencesApi,
    emailStyle: EmailStyleApi,
    emailTemplate: EmailTemplateApi,
    exam: ExamApi,
    followUp: FollowUpApi,
    analytics: AnalyticsApi,
    insights: InsightsApi,
    interest: InterestApi,
    invites: InvitesApi,
    liveTranscribe: LiveTranscribeApi,
    liveWebinar: LiveWebinarApi,
    login: LoginApi,
    onDemandWebinar: OnDemandWebinarApi,
    poll: PollApi,
    slideDeck: SlideDeckApi,
    subtitles: SubtitlesApi,
    subtitleGenerator: SubtitlesGeneratorApi,
    survey: SurveyApi,
    testSuite: TestSuiteApi,
    tier: TierApi,
    translation: TranslationApi,
    unsubscribe: UnsubscribeApi,
    usageStatistics: UsageStatisticsApi,
    videoEditor: VideoEditorApi,
    watchLink: WatchLinkApi,
    webinar: WebinarApi,
    webinarAttachments: WebinarAttachmentsApi,
    webinars: WebinarsApi,
    webinarSecurity: WebinarSecurityApi
};
